import { AfterViewInit, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { AutomationFootprintVersion } from '@prism/prism-shared';
import { renderFootprintSymbol } from './symbol-render.util';

type G = d3.Selection<SVGGElement, unknown, null, undefined>;

/**
 * Presentational SVG render of a footprint version's symbol. Auto-fits the viewBox to the drawn
 * geometry (via getBBox) so the symbol is centered regardless of where it was drawn in world space.
 * Reused on browse cards (small), the footprint view (large), and hardware-item views.
 */
@Component({
    selector: 'prism-footprint-version-preview',
    templateUrl: './footprint-version-preview.component.html',
    styleUrls: ['./footprint-version-preview.component.scss'],
})
export class FootprintVersionPreviewComponent implements AfterViewInit, OnChanges {
    @ViewChild('symbolSvg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

    @Input() footprintVersion: AutomationFootprintVersion | null = null;
    @Input() coloredPins = false;
    @Input() showPinLabels = false;

    private viewReady = false;

    ngAfterViewInit(): void {
        this.viewReady = true;
        this.render();
    }

    ngOnChanges(): void {
        if (this.viewReady) this.render();
    }

    private render(): void {
        const svg = d3.select(this.svgRef.nativeElement);
        svg.selectAll('*').remove();
        const def = this.footprintVersion;
        if (!def) return;

        const g = svg.append('g') as unknown as G;
        renderFootprintSymbol(g, def, { coloredPins: this.coloredPins, showPinLabels: this.showPinLabels });

        const PAD = 6;
        let vbX: number, vbY: number, vbW: number, vbH: number;
        try {
            const b = (g.node() as SVGGElement).getBBox();
            vbX = b.x - PAD; vbY = b.y - PAD; vbW = b.width + PAD * 2; vbH = b.height + PAD * 2;
        } catch {
            vbX = 0; vbY = 0; vbW = def.width || 80; vbH = def.height || 60;
        }
        if (!isFinite(vbW) || vbW <= 0) vbW = def.width || 80;
        if (!isFinite(vbH) || vbH <= 0) vbH = def.height || 60;
        svg.attr('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`)
            .attr('preserveAspectRatio', 'xMidYMid meet');
    }
}
